import { useCallback } from "react";
import { Avatar, Chip, Link, Tooltip } from "@heroui/react";
import type { BlacklistItem } from "@repo/api/blacklist.type";
import { UserAvatar } from "../../../components/User";
import { useUnsubscribeBlacklist } from "../blacklist/use-blacklist";

interface SubscribedBlacklistsProps {
  items: BlacklistItem[];
  loading?: boolean;
  onNavigateSelfBlacklist: () => void;
  onNavigatePublicBlacklist: (id: string) => void;
  onRemoved?: (id: string) => void;
}

export function SubscribedBlacklists({
  items,
  loading,
  onNavigateSelfBlacklist,
  onNavigatePublicBlacklist,
}: SubscribedBlacklistsProps) {
  if (loading) {
    return (
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-gray-200" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Tooltip>
        <Tooltip.Trigger>
          <Chip key={0} variant="soft" color="accent" className="cursor-not-allowed rounded-none">
            兽聚汇官方数据库
          </Chip>
        </Tooltip.Trigger>
        <Tooltip.Content showArrow>
          <Tooltip.Arrow />
          官方数据库不可移除
        </Tooltip.Content>
      </Tooltip>

      <Chip
        key={1}
        variant="soft"
        color="default"
        className="cursor-pointer rounded-none transition-all hover:bg-gray-300"
        onClick={() => onNavigateSelfBlacklist()}
      >
        我的黑名单
      </Chip>
      {items.map((item) => (
        <Chip
          key={item.id}
          variant="soft"
          color="default"
          className="cursor-pointer rounded-none transition-all hover:bg-gray-300"
          onClick={() => onNavigatePublicBlacklist(item.id)}
        >
          <Avatar className="mr-1 inline-block h-4 w-4">
            <Avatar.Image src={item.avatar_url} />
          </Avatar>
          {`${item.nickname} 的黑名单`}
        </Chip>
      ))}
    </div>
  );
}

export function UnavailableBlacklists({
  items,
  loading,
  onRemoved,
}: Pick<SubscribedBlacklistsProps, "items" | "loading" | "onRemoved">) {
  // 取消订阅成功后订阅列表缓存自动失效，不需要调用方手动从列表里删
  const unsubscribe = useUnsubscribeBlacklist();
  const handleUnsubscribe = useCallback(
    (item: BlacklistItem) => {
      unsubscribe.mutate(item.id, { onSuccess: () => onRemoved?.(item.id) });
    },
    [onRemoved, unsubscribe],
  );

  if (loading) {
    return (
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-gray-200" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Chip key={item.id} variant="soft" color="default" className="transition-all hover:bg-gray-300">
          <UserAvatar avatar_url={item.avatar_url} size="sm" />
          {`${item.nickname} 的黑名单`}
          <Link
            className="ml-2 text-xs text-gray-500 transition-all hover:cursor-pointer hover:text-red-500"
            onPress={() => handleUnsubscribe(item)}
          >
            移除
          </Link>
        </Chip>
      ))}
    </div>
  );
}
